import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Badge, Dropdown } from "antd";
import { Icon } from "@iconify/react";
import { Button } from "@/shared/components/button";
import { cn } from "@/shared/lib/utils";
import { formatRelativeTime } from "@/shared/lib/formatRelativeTime";
import { toastError } from "@/shared/lib/toast";
import {
  acceptAccessRequestApi,
  acceptIncomingPatientRequestApi,
  approveCategoryRequestApi,
  declineAccessRequestApi,
  declineCategoryRequestApi,
  declineIncomingPatientRequestApi,
  listIncomingPatientRequestsApi,
} from "@/portals/patient/api/familyApi";
import type { IncomingPatientAccessRequest } from "@/shared/types/family";
import { ApproveCaregiverAccessModal } from "@/features/patient/family/components/ApproveCaregiverAccessModal";
import { playNotificationSound } from "@/lib/notificationSound";
import { useRefetchOnFocusAndReconnect } from "@/lib/query/useRefetchOnFocusAndReconnect";
import { useNotificationsStore, type AppNotification, type NotificationsStore, type NotificationType } from "@/stores/notificationsStore";
import { getClinicianNotificationsStore } from "@/stores/clinicianNotificationsStore";
import { getAdminNotificationsStore } from "@/stores/adminNotificationsStore";

type NotificationAction = "approve" | "deny";

interface ActionHandlers {
  /** Which field of notification.data carries the id these handlers act on. */
  idField: "relationshipId" | "categoryRequestId";
  accept: (id: string) => Promise<{ error: string | null }>;
  decline: (id: string) => Promise<{ error: string | null }>;
}

// Only these three types are ever something the recipient needs to act on —
// every other type (accepted/declined/cancelled/revoked/expiring) is purely
// informational, telling you what someone else already decided or what's
// about to happen on its own.
const ACTIONABLE_HANDLERS: Partial<Record<NotificationType, ActionHandlers>> = {
  CAREGIVER_INVITE_RECEIVED: {
    idField: "relationshipId",
    accept: (id) => acceptAccessRequestApi(id).then(({ error }) => ({ error })),
    decline: (id) => declineAccessRequestApi(id).then(({ error }) => ({ error })),
  },
  ACCESS_REQUEST_RECEIVED: {
    idField: "relationshipId",
    // accept is intentionally unused for this type — see NEEDS_APPROVAL_MODAL below.
    // Kept here so decline (and the canAct/idField lookup) still works generically.
    accept: (id) => acceptIncomingPatientRequestApi(id).then(({ error }) => ({ error })),
    decline: (id) => declineIncomingPatientRequestApi(id).then(({ error }) => ({ error })),
  },
  ACCESS_CATEGORY_REQUESTED: {
    idField: "categoryRequestId",
    accept: (id) => approveCategoryRequestApi(id).then(({ error }) => ({ error })),
    decline: (id) => declineCategoryRequestApi(id).then(({ error }) => ({ error })),
  },
};

// Direction-A "a caregiver wants to view your records" requests need the patient to
// pick which categories to share before anything is granted. The backend's raw accept
// endpoint grants every category by default (see accept_incoming_patient_request) —
// the dashboard's own normal flow (AccessRequestsBanner) never calls it directly for
// exactly this reason; it only opens ApproveCaregiverAccessModal and lets the modal's
// submit narrow access down in the same breath. Approving straight from the
// notification bell must go through that same modal instead of granting full access
// on a single click. The other two actionable types have no such gap: a caregiver
// invite's categories were already fixed by the inviting patient, and a category
// request grants exactly the one specific category asked for — nothing to narrow.
const NEEDS_APPROVAL_MODAL: ReadonlySet<NotificationType> = new Set(["ACCESS_REQUEST_RECEIVED"]);

type Variant = "patient" | "clinician" | "admin";

interface VariantConfig {
  store: () => NotificationsStore;
  /** Only allowlisted types are listed and counted toward the unread dot. */
  visibleTypes: ReadonlySet<NotificationType>;
  /** Where clicking a notification goes, or null to only mark it read. */
  target: (notification: AppNotification) => string | null;
}

const VARIANTS: Record<Variant, VariantConfig> = {
  // The caregiver/dedicated-profile module is still hidden from end users, so its
  // notification types stay invisible here exactly as when the whole bell was off.
  // A review notification opens that profile's medications. The profile switch
  // itself happens on the patient side (MedicationsPage reads ?profile=), since
  // this menu also renders in headers outside FamilyProvider.
  patient: {
    store: () => useNotificationsStore,
    visibleTypes: new Set<NotificationType>([
      "REVIEW_ITEM_NEEDS_INPUT", "REVIEW_ITEM_QUESTION", "REVIEW_ITEM_RESOLVED", "IDENTITY_UNCONFIRMED",
    ]),
    target: (n) => {
      const profileId = n.data?.profileId;
      const profileQuery = profileId ? `?profile=${encodeURIComponent(profileId)}` : "";
      const itemId = n.data?.reviewItemId ? encodeURIComponent(n.data.reviewItemId) : null;
      const withProfile = (path: string) => (profileId ? `${path}&profile=${encodeURIComponent(profileId)}` : path);
      if (n.type === "REVIEW_ITEM_NEEDS_INPUT") return `/patient/human-review-queue${profileQuery}`;
      // Anything still needing the patient lives in the Human Review Queue — a
      // clinician's question opens that item there, with the conversation.
      if (n.type === "REVIEW_ITEM_QUESTION" && itemId) return withProfile(`/patient/human-review-queue?item=${itemId}`);
      // A finished review belongs to the Medications tab: open its outcome there.
      if (n.type === "REVIEW_ITEM_RESOLVED" && itemId) return withProfile(`/patient/medications?review=${itemId}`);
      // An upload we couldn't confirm belongs to this profile: the patient answers
      // "Is this record yours?" in the Patient Only Queue (that item, when known).
      if (n.type === "IDENTITY_UNCONFIRMED") {
        return itemId ? withProfile(`/patient/patient-only-queue?item=${itemId}`) : `/patient/patient-only-queue${profileQuery}`;
      }
      return null;
    },
  },
  // The clinician feed (its own store, socket and endpoints) — both types open the item.
  clinician: {
    store: getClinicianNotificationsStore,
    visibleTypes: new Set<NotificationType>(["REVIEW_ITEM_ASSIGNED", "REVIEW_ITEM_PATIENT_REPLIED", "REVIEW_ITEM_CLOSED_BY_PATIENT"]),
    // A closed item has nothing to open any more — that one goes to My Items.
    target: (n) =>
      n.type === "REVIEW_ITEM_CLOSED_BY_PATIENT"
        ? "/clinician/my-items"
        : n.data?.reviewItemId ? `/clinician/review/${encodeURIComponent(n.data.reviewItemId)}` : null,
  },
  // The admin feed (Module 6) — its own store, socket and endpoints.
  admin: {
    store: getAdminNotificationsStore,
    visibleTypes: new Set<NotificationType>(["REVIEW_QUEUE_ITEMS_ADDED", "UNCLAIMED_ITEMS_ALERT", "CLINICIAN_INVITE_ACCEPTED", "CLINICIAN_INVITE_DECLINED"]),
    target: (n) =>
      n.type === "REVIEW_QUEUE_ITEMS_ADDED" || n.type === "UNCLAIMED_ITEMS_ALERT"
        ? "/admin/review-queue"
        : n.type === "CLINICIAN_INVITE_ACCEPTED" || n.type === "CLINICIAN_INVITE_DECLINED"
          ? "/admin/clinicians"
          : null,
  },
};

export function NotificationsMenu({ variant = "patient" }: { variant?: Variant }) {
  const config = VARIANTS[variant];
  const [open, setOpen] = useState(false);
  // Tracks which notification AND which of its two buttons is in flight, so only
  // the clicked button shows a spinner while both are disabled — otherwise there's
  // no feedback that a request is processing, and a second click (approve then
  // deny, or a double-click) could race against the first.
  const [pending, setPending] = useState<{ notificationId: string; action: NotificationAction } | null>(null);
  // Holds the full request (fetched fresh — the notification payload only carries
  // the id) while ApproveCaregiverAccessModal is open for a notification's Approve click.
  const [approvalTarget, setApprovalTarget] = useState<{
    notificationId: string;
    request: IncomingPatientAccessRequest;
  } | null>(null);
  const { notifications: allNotifications, markRead, remove, fetch, lastArrivalAt } = config.store()();
  const notifications = allNotifications.filter((n) => config.visibleTypes.has(n.type));
  const unreadCount = notifications.filter((n) => !n.isRead).length;
  const navigate = useNavigate();

  // One-off attention cue: ring the bell (and play the chime) when a NEW unread
  // notification of a type this bell shows arrives live — not on page load,
  // never on a loop.
  const [ringKey, setRingKey] = useState(0);
  const [rippleKey, setRippleKey] = useState(0);
  const seenUnread = useRef<Set<string> | null>(null);
  const visibleUnreadIds = notifications.filter((n) => !n.isRead).map((n) => n.id);
  useEffect(() => {
    const previous = seenUnread.current;
    seenUnread.current = new Set(visibleUnreadIds);
    if (!previous || !lastArrivalAt) return;
    if (visibleUnreadIds.some((id) => !previous.has(id))) {
      setRingKey((k) => k + 1);
      setRippleKey(Date.now());
      playNotificationSound();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed on the joined ids
  }, [visibleUnreadIds.join(","), lastArrivalAt]);

  const handleOpen = (notification: AppNotification) => {
    void markRead(notification.id);
    const to = config.target(notification);
    if (!to) return;
    setOpen(false);
    navigate(to);
  };

  // The store's socket listener (see notificationsStore.ts) is the instant
  // path, and a reconnect / server resync refetches the list. Coming back to
  // the tab, or back online, also refetches — no interval polling.
  useRefetchOnFocusAndReconnect(fetch);

  const handleAction = async (notification: AppNotification, action: NotificationAction) => {
    const handlers = ACTIONABLE_HANDLERS[notification.type];
    const targetId = handlers ? notification.data?.[handlers.idField] : undefined;
    if (!targetId || !handlers) return;

    if (action === "approve" && NEEDS_APPROVAL_MODAL.has(notification.type)) {
      setPending({ notificationId: notification.id, action });
      const { data, error } = await listIncomingPatientRequestsApi();
      const request = data?.find((r) => r.id === targetId) ?? null;
      setPending(null);
      if (error || !request) {
        toastError(error ?? "This request is no longer available.");
        return;
      }
      setApprovalTarget({ notificationId: notification.id, request });
      setOpen(false);
      return;
    }

    setPending({ notificationId: notification.id, action });
    const { error } = action === "approve" ? await handlers.accept(targetId) : await handlers.decline(targetId);
    setPending(null);
    if (error) {
      toastError(error);
      return;
    }
    remove(notification.id);
  };

  const panel = (
    // Opens right-aligned to the bell, which sits ~60px from the screen edge (avatar
    // + padding): cap the width to what's left of it so it never runs off the left.
    <div className="w-[min(620px,calc(100vw-84px))] overflow-hidden rounded-[18px] bg-white shadow-[0_18px_55px_rgba(24,0,71,0.22)]">
      <div className="flex items-center justify-between px-6 pb-3 pt-5">
        <div className="flex items-center gap-3">
          <h2 className="text-[18px] font-extrabold text-[#111111]">Notifications</h2>
          {unreadCount > 0 && (
            <span className="flex min-w-7 items-center justify-center rounded bg-[#F17D95] px-1.5 py-1 text-xs font-bold text-white">
              {unreadCount}
            </span>
          )}
        </div>
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setOpen(false)}
          aria-label="Close notifications"
          className="!h-8 !w-8 !rounded-lg !text-black hover:!bg-[#F5F5F5] hover:!text-black"
        >
          <Icon icon="lucide:x" className="h-6 w-6" />
        </Button>
      </div>

      <div className="mx-6 h-px bg-[#E5E7EB]" />

      <div className="max-h-[390px] space-y-3 overflow-y-auto px-4 py-5 [scrollbar-color:#D6D9E0_transparent] [scrollbar-width:thin]">
        {notifications.length === 0 ? (
          <div className="flex min-h-40 flex-col items-center justify-center text-center">
            <Icon icon="lucide:bell-off" className="h-8 w-8 text-[#A0A7B3]" />
            <p className="mt-3 text-sm font-bold text-[#252B3A]">No notifications</p>
          </div>
        ) : (
          notifications.map((notification) => {
            const handlers = ACTIONABLE_HANDLERS[notification.type];
            const canAct = Boolean(handlers && notification.data?.[handlers.idField]);
            const isThisPending = pending?.notificationId === notification.id;
            const isApprovePending = isThisPending && pending?.action === "approve";
            const isDenyPending = isThisPending && pending?.action === "deny";
            return (
              <article
                key={notification.id}
                onClick={() => handleOpen(notification)}
                className={cn(
                  "cursor-pointer rounded-[16px] border px-[14px] py-3.5 transition",
                  notification.isRead ? "border-[#E5E7EB]" : "border-[#F17D95]",
                )}
              >
                <div className="flex items-start justify-between gap-5">
                  <div className="min-w-0">
                    <p className="text-[14px] font-bold leading-5 tracking-[0.02em] text-[#303747]">{notification.title}</p>
                    <p className="mt-0.5 text-[13px] leading-5 text-[#4B5563]">{notification.body}</p>
                  </div>
                  <time className="shrink-0 pt-0.5 text-xs text-[#303747]">{formatRelativeTime(notification.createdAt)}</time>
                </div>
                {canAct && (
                  <div className="mt-3 flex items-center gap-2" onClick={(event) => event.stopPropagation()}>
                    <Button
                      variant="default"
                      disabled={isThisPending}
                      loading={isApprovePending}
                      onClick={() => void handleAction(notification, "approve")}
                      className="!h-8 !rounded-md !border-[#F17D95] !bg-[#F17D95] !px-3 !text-xs !font-semibold !text-white hover:!border-[#EC6B87] hover:!bg-[#EC6B87] hover:!text-white"
                    >
                      Approve
                    </Button>
                    <Button
                      variant="outline"
                      disabled={isThisPending}
                      loading={isDenyPending}
                      onClick={() => void handleAction(notification, "deny")}
                      className="!h-8 !rounded-md !border-[#F17D95] !bg-white !px-3 !text-xs !font-semibold !text-[#F17D95] hover:!border-[#F17D95] hover:!bg-[#FFF4F6] hover:!text-[#F17D95]"
                    >
                      Deny
                    </Button>
                  </div>
                )}
              </article>
            );
          })
        )}
      </div>
    </div>
  );

  return (
    <>
      <Dropdown
        open={open}
        onOpenChange={setOpen}
        trigger={["click"]}
        placement="bottomRight"
        popupRender={() => panel}
      >
        <button type="button" aria-label="Open notifications" className="relative block rounded-full">
          {/* Drawn before the badge so it ripples BEHIND the count, then removed. */}
          {rippleKey > 0 && unreadCount > 0 && (
            <span
              key={rippleKey}
              aria-hidden
              onAnimationEnd={() => setRippleKey(0)}
              className="animate-bell-ripple pointer-events-none absolute -right-0.5 -top-0.5 h-4 w-4 rounded-full bg-[#C8102E]"
            />
          )}
          {/* The real unread count (99+ only past 99); antd draws it with a white ring. */}
          <Badge
            count={unreadCount}
            overflowCount={99}
            size="small"
            color="#C8102E"
            offset={[-3, 3]}
            title={`${unreadCount} unread notification${unreadCount === 1 ? "" : "s"}`}
            className="[&_.ant-badge-count]:!font-bold"
          >
            <span className="flex h-9 w-9 items-center justify-center rounded-full border border-black transition-colors hover:bg-[#F5F5F5]">
              {/* Re-keyed per arrival so the one-off ring replays for each new notification. */}
              <span
                key={ringKey}
                data-testid={ringKey ? "bell-ringing" : undefined}
                className={cn("flex", ringKey > 0 && "animate-bell-ring")}
              >
                <Icon icon="lucide:bell" className="h-5 w-5 text-[#3A3D42]" />
              </span>
            </span>
          </Badge>
        </button>
      </Dropdown>
      {/* Caregiver approvals stay off with the rest of that module (their types are
          filtered out above) — restore this modal when VISIBLE_TYPES includes them. */}
      {/* <ApproveCaregiverAccessModal
        open={approvalTarget !== null}
        request={approvalTarget?.request ?? null}
        onOpenChange={(nextOpen) => { if (!nextOpen) setApprovalTarget(null); }}
        onApproved={() => {
          if (approvalTarget) remove(approvalTarget.notificationId);
          setApprovalTarget(null);
        }}
      /> */}
    </>
  );
}
